import {useState, useCallback, useMemo, useEffect} from 'react'
import {useBi, useEnvironment} from '@wix/yoshi-flow-editor'
import {TabItem} from 'wix-ui-tpa/cssVars'
import {Experience as ExperienceProto} from '@wix/ambassador-table-reservations-v1-experience/types'
import {ReservationLocation as ReservationLocationProto} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import {useExperiencesAddOnStorage} from '../storage'
import {RequestStatus} from '../../../utils/wrapRequest'
import {getLogger, PageType} from '../../../utils/getLogger'
import {AppPage} from '../../../utils/constants'
import {isExperienceInPast} from '../../../utils/experienceStatus'
import {sortExperiencesAlphabetically, sortExperiencesManually} from '../../../utils/sortExperiences'
import {
  ExperiencesDisplayMode,
  ExperienceSortOrder,
  ExperienceStatusFilter,
  ExperiencesFilterConfig,
} from '../Settings/Tabs/Experiences/settingsParams'

const ALL_RESERVATION_LOCATIONS_OPTION = 0

export const useHooks = ({
  allReservationLocationsTabText,
}: {
  allReservationLocationsTabText: string
}) => {
  const {isPreview, isEditor} = useEnvironment()
  const bi = useBi()
  const logger = getLogger(bi)
  const {
    regionalSettings,
    reservationLocations: allReservationLocations,
    experiences: allExperiences,
    getReservationLocationsStatus,
    getExperiencesStatus,
    goToExperienceDetailsPage,
    filterConfig,
  } = useExperiencesAddOnStorage()

  const [selectedReservationLocationTabIndex, setSelectedReservationLocationTabIndex] = useState(
    ALL_RESERVATION_LOCATIONS_OPTION,
  )

  const {reservationLocationsMap, filteredExperiences} = useMemo(
    () =>
      filterReservationLocationsAndExperiences(
        allReservationLocations,
        allExperiences,
        filterConfig,
        isEditor || isPreview,
      ),
    [allReservationLocations, allExperiences, filterConfig, isEditor, isPreview],
  )

  const reservationLocationTabOptions: TabItem[] = useMemo(() => {
    const allReservationLocationsOption: TabItem = {
      id: 'all',
      title: allReservationLocationsTabText,
    }

    const restReservationLocationOptions = Object.values(reservationLocationsMap).map(
      ({id, location}) => ({
        id: id!,
        title: location?.name!,
      }),
    )

    return [allReservationLocationsOption, ...restReservationLocationOptions]
  }, [allReservationLocationsTabText, reservationLocationsMap])

  const selectedExperiences =
    selectedReservationLocationTabIndex === ALL_RESERVATION_LOCATIONS_OPTION
      ? filteredExperiences
      : filteredExperiences.filter(
          ({reservationLocationId}) =>
            reservationLocationId ===
            reservationLocationTabOptions[selectedReservationLocationTabIndex].id,
        )

  const showErrorState =
    getReservationLocationsStatus === RequestStatus.FAILED ||
    getExperiencesStatus === RequestStatus.FAILED
  const showEmptyState = !showErrorState && selectedExperiences.length === 0

  const handleReservationLocationSelect = useCallback((index: number) => {
    setSelectedReservationLocationTabIndex(index)
  }, [])

  const handleGoToExperience = useCallback(
    (experience: ExperienceProto) => {
      goToExperienceDetailsPage(experience)
    },
    [goToExperienceDetailsPage],
  )

  useEffect(() => {
    logger.isLoaded({
      isAddOn: true,
      isPreview,
      pageType: PageType.page,
      pageName: AppPage.ExperiencesList,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    regionalSettings,
    selectedReservationLocationTabIndex,
    reservationLocationTabOptions,
    selectedExperiences,
    showErrorState,
    showEmptyState,
    handleReservationLocationSelect,
    handleGoToExperience,
  }
}

const applyStatusFilter = (
  experiences: ExperienceProto[],
  statusFilter: ExperienceStatusFilter,
): ExperienceProto[] => {
  if (statusFilter === ExperienceStatusFilter.UpcomingAndPast) {
    return experiences
  }

  return experiences.filter((experience) => {
    const isPast = isExperienceInPast(experience)
    return statusFilter === ExperienceStatusFilter.Past ? isPast : !isPast
  })
}

const filterReservationLocationsAndExperiences = (
  reservationLocations: ReservationLocationProto[],
  experiences: ExperienceProto[],
  filterConfig: ExperiencesFilterConfig,
  isEditorOrPreview: boolean,
) => {
  const unarchivedReservationLocations = reservationLocations.filter(({archived}) => !archived)
  const visibleExperiences = experiences.filter(({configuration}) => configuration?.visible)

  const {displayMode, statusFilter, sortOrder, manualOrder} = filterConfig

  const enabledLocationIds =
    displayMode === ExperiencesDisplayMode.ByLocation ? filterConfig.enabledLocationIds : undefined
  const enabledExperienceIds =
    displayMode === ExperiencesDisplayMode.Custom ? filterConfig.enabledExperienceIds : undefined

  const locationIdSet = enabledLocationIds ? new Set(enabledLocationIds) : undefined
  const experienceIdSet = enabledExperienceIds ? new Set(enabledExperienceIds) : undefined

  const reservationLocationsMap: Record<string, ReservationLocationProto> = {}

  let filteredExperiences = visibleExperiences.filter((experience) => {
    const {reservationLocationId} = experience

    if (experienceIdSet && !experienceIdSet.has(experience.id!)) {
      return false
    }

    if (!reservationLocationId) {
      return isEditorOrPreview && !locationIdSet
    }

    const experienceReservationLocation = unarchivedReservationLocations.find(
      ({id}) => id === reservationLocationId,
    )

    if (!experienceReservationLocation) {
      return false
    }

    if (locationIdSet && !locationIdSet.has(experienceReservationLocation.id!)) {
      return false
    }

    reservationLocationsMap[experienceReservationLocation.id!] = experienceReservationLocation
    return true
  })

  filteredExperiences = applyStatusFilter(filteredExperiences, statusFilter)

  if (displayMode === ExperiencesDisplayMode.Custom && sortOrder === ExperienceSortOrder.Manually) {
    filteredExperiences = sortExperiencesManually(filteredExperiences, manualOrder)
  } else {
    filteredExperiences = sortExperiencesAlphabetically(filteredExperiences)
  }

  return {
    reservationLocationsMap,
    filteredExperiences,
  }
}
